import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from 'core/components/Spinner';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { showGlobalModal } from 'core/components/GlobalModal';
import { useBlogActions } from '../../hooks/useBlogActions';
import { useAIGenerator } from '../../hooks/useAIGenerator';

import { BlogDashboardView } from './components/BlogDashboardView';
import { BlogFormView } from './components/BlogFormView';
import { BlogPreviewView } from './components/BlogPreviewView';
import { GeneralModals, BlogModalState } from './components/GeneralModals';
import type { BlogItem } from 'features/DigitalMarketing/slice/types';
import type { BlogForm, BlogPublishFilter } from './blogUtils';
import {
  buildBlogUpsertPayload,
  emptyBlogForm,
  extractGeneratedImageUrl,
  getBlogFormFromBlog,
  getBlogId,
  getBlogRouteState,
  isBlogPublished,
  getCacheBustedImageUrl,
  hasBlogFormChanges,
} from './blogUtils';
import { DMDiv } from '../../components/Primitives';

const IMAGE_READINESS_CHECK_INTERVAL_MS = 3000;
const MAX_IMAGE_READINESS_ATTEMPTS = 20;

// Main Blog Content Component handling Dashboard, Create/Edit and Preview views
export default function BlogContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const authData = useAuth();

  const pathname = location.pathname;
  const {
    currentView,
    isEditMode,
    isPreviewView,
    blogId: blogIdFromUrl,
    hasIdInUrl,
  } = getBlogRouteState(pathname);

  const [activeModal, setActiveModal] = useState<BlogModalState>(null);
  const [warningMessage, setWarningMessage] = useState('');
  const [hasAIGeneratedText, setHasAIGeneratedText] = useState(
    () => location.state?.hasAIGeneratedText || false
  );
  const [hasAIGeneratedImage, setHasAIGeneratedImage] = useState(
    () => location.state?.hasAIGeneratedImage || false
  );
  const [isVerifyingImage, setIsVerifyingImage] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [isPublishingFlow, setIsPublishingFlow] = useState(false);
  const hasRequestedPublishAfterUpsert = useRef(false);

  const [blogForm, setBlogForm] = useState<BlogForm>(
    () => location.state?.blogForm || emptyBlogForm
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBlogId, setSelectedBlogId] = useState<number | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    blogsList,
    currentBlog,
    fetchBlogs,
    fetchBlogById,
    upsertBlog,
    upsertSuccess,
    publishBlog,
    publishSuccess,
    deleteBlog,
    deleteSuccess,
    publishFilter,
    page,
    pageSize,
    totalCount,
    clearState,
    upsertedBlogId,
    lastPublishStatus,
    isLoadingBlogs,
    isLoadingBlogView,
    isUpsertingBlog,
    isPublishingBlog,
    isDeletingBlog,
  } = useBlogActions();

  const {
    generateText,
    generateImage,
    enhanceImage,
    isGeneratingAIText,
    generatedTextData,
    isGeneratingAIImage,
    generatedImageUrl,
    isEnhancingImage,
  } = useAIGenerator();

  useEffect(() => {
    if (
      authData?.INSTITUTE_ID &&
      authData?.BRANCH_ID &&
      currentView === 'dashboard'
    ) {
      fetchBlogs(
        authData.INSTITUTE_ID,
        authData.BRANCH_ID,
        publishFilter,
        page,
        pageSize
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authData?.INSTITUTE_ID,
    authData?.BRANCH_ID,
    publishFilter,
    page,
    pageSize,
    currentView,
    fetchBlogs,
    refreshKey,
  ]);

  // Track AI Generation
  useEffect(() => {
    if (generatedTextData?.content) {
      setHasAIGeneratedText(true);
      setBlogForm(prev => ({
        ...prev,
        content: generatedTextData.content,
        title: prev.title || generatedTextData.title || '',
      }));
    }
  }, [generatedTextData]);

  useEffect(() => {
    if (generatedImageUrl) {
      setHasAIGeneratedImage(true);
      setIsVerifyingImage(true);
      setVerificationError(false);

      const finalUrl = extractGeneratedImageUrl(generatedImageUrl);

      if (finalUrl) {
        let attempts = 0;

        const checkImage = () => {
          const img = new Image();
          img.onload = () => {
            setBlogForm(prev => ({ ...prev, image: finalUrl }));
            setIsVerifyingImage(false);
          };
          img.onerror = () => {
            attempts++;
            if (attempts < MAX_IMAGE_READINESS_ATTEMPTS) {
              setTimeout(checkImage, IMAGE_READINESS_CHECK_INTERVAL_MS);
            } else {
              setIsVerifyingImage(false);
              setVerificationError(true);
            }
          };
          img.src = getCacheBustedImageUrl(finalUrl);
        };

        checkImage();
      } else {
        setIsVerifyingImage(false);
      }
    }
  }, [generatedImageUrl]);

  // Handle Edit Mode or Preview Mode with ID prepopulation
  useEffect(() => {
    if (isEditMode || (isPreviewView && blogIdFromUrl)) {
      setSelectedBlogId(blogIdFromUrl);
      setBlogForm(emptyBlogForm);
      fetchBlogById(blogIdFromUrl);
    } else if (currentView === 'create' && !selectedBlogId) {
      if (location.state?.restore) {
        if (location.state?.blogForm) {
          setBlogForm(location.state.blogForm);
          setHasAIGeneratedText(location.state.hasAIGeneratedText || false);
          setHasAIGeneratedImage(location.state.hasAIGeneratedImage || false);
        }
      } else {
        setBlogForm(emptyBlogForm);
        setHasAIGeneratedText(false);
        setHasAIGeneratedImage(false);
      }
    }
  }, [
    isEditMode,
    isPreviewView,
    blogIdFromUrl,
    fetchBlogById,
    currentView,
    location.state,
    selectedBlogId,
  ]);

  useEffect(() => {
    if (currentBlog && (isEditMode || (isPreviewView && blogIdFromUrl))) {
      setSelectedBlogId(getBlogId(currentBlog) ?? blogIdFromUrl);
      setBlogForm(getBlogFormFromBlog(currentBlog));
    }
  }, [isEditMode, isPreviewView, blogIdFromUrl, currentBlog]);

  // Success listeners
  useEffect(() => {
    if (upsertSuccess) {
      if (!isPublishingFlow) {
        navigate('/digital-marketing/blog');
        clearState();
      } else if (!hasRequestedPublishAfterUpsert.current) {
        const publishId = selectedBlogId ?? upsertedBlogId;
        if (!publishId) {
          setIsPublishingFlow(false);
          setWarningMessage(
            'Blog saved, but no blog ID was returned for publishing.'
          );
          setActiveModal('warning');
          clearState();
          return;
        }

        hasRequestedPublishAfterUpsert.current = true;
        setSelectedBlogId(publishId);
        publishBlog(publishId, true);
      }
    }
  }, [
    upsertSuccess,
    navigate,
    clearState,
    isPublishingFlow,
    selectedBlogId,
    upsertedBlogId,
    publishBlog,
  ]);

  useEffect(() => {
    if (publishSuccess) {
      setIsPublishingFlow(false);
      setActiveModal(
        lastPublishStatus === false ? 'success_unpublish' : 'success_publish'
      );
      setTimeout(() => {
        setActiveModal(null);
        clearState();
        if (currentView === 'dashboard') {
          // Already on dashboard — bump refreshKey to trigger a single re-fetch
          setRefreshKey(k => k + 1);
        } else {
          // Navigate to dashboard — the main useEffect will fetch once currentView changes
          navigate('/digital-marketing/blog');
        }
      }, 2000);
    }
  }, [publishSuccess, navigate, clearState, lastPublishStatus, currentView]);

  useEffect(() => {
    if (deleteSuccess) {
      setActiveModal('success_delete');
      setTimeout(() => {
        setActiveModal(null);
        clearState();
        // Delete only happens on dashboard — bump refreshKey to trigger a single re-fetch
        setRefreshKey(k => k + 1);
      }, 2000);
    }
  }, [deleteSuccess, clearState]);

  const handleGenerateText = () => {
    if (blogForm.title && blogForm.description) {
      generateText(blogForm.title, blogForm.description);
    } else {
      setWarningMessage(
        'Please enter title and description for AI to generate content.'
      );
      setActiveModal('warning');
    }
  };

  const handleFilterChange = (filter: BlogPublishFilter) => {
    if (authData?.INSTITUTE_ID && authData?.BRANCH_ID) {
      fetchBlogs(
        authData.INSTITUTE_ID,
        authData.BRANCH_ID,
        filter,
        1,
        pageSize
      );
    }
  };

  const handleBlogPageChange = (nextPage: number) => {
    if (authData?.INSTITUTE_ID && authData?.BRANCH_ID) {
      fetchBlogs(
        authData.INSTITUTE_ID,
        authData.BRANCH_ID,
        publishFilter,
        nextPage,
        pageSize
      );
    }
  };

  const handleBlogPageSizeChange = (nextPageSize: number) => {
    if (authData?.INSTITUTE_ID && authData?.BRANCH_ID) {
      fetchBlogs(
        authData.INSTITUTE_ID,
        authData.BRANCH_ID,
        publishFilter,
        1,
        nextPageSize
      );
    }
  };

  const handleGenerateImage = () => {
    if (blogForm.title && blogForm.description) {
      generateImage(blogForm.title, blogForm.description);
    } else {
      setWarningMessage(
        'Please enter title and description for AI to generate image.'
      );
      setActiveModal('warning');
    }
  };

  const requestPublishStatusChange = (
    blogId: number | null | undefined,
    shouldPublish: boolean
  ) => {
    if (!blogId) {
      setWarningMessage('Blog id is missing.');
      setActiveModal('warning');
      return;
    }

    showGlobalModal(
      shouldPublish
        ? 'Are you sure you want to publish this blog?'
        : 'Are you sure you want to unpublish this blog?',
      'warning',
      {
        showConfirm: true,
        onConfirm: () => publishBlog(blogId, shouldPublish),
        onCancel: () => undefined,
        confimLabel: shouldPublish ? 'Publish' : 'Unpublish',
        cancelLabel: 'Cancel',
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (selectedBlogId) {
      deleteBlog(selectedBlogId);
    }
  };

  const handleSaveDraft = () => {
    upsertBlog(
      buildBlogUpsertPayload({
        form: blogForm,
        blogId: selectedBlogId,
      })
    );
  };

  const handleEdit = (blog: BlogItem) => {
    const validId = getBlogId(blog);
    navigate(`/digital-marketing/blog/edit/${validId}`);
  };

  const handlePreview = (blog: BlogItem) => {
    const validId = getBlogId(blog);
    const isPublished = isBlogPublished(blog);
    navigate(`/digital-marketing/blog/preview/${validId}`, {
      state: { from: 'dashboard', isPublished },
    });
  };

  const fromDashboard = location.state?.from === 'dashboard';
  const isPublished = Boolean(
    location.state?.isPublished || isBlogPublished(currentBlog)
  );
  const shouldGoToDashboard =
    fromDashboard || (hasIdInUrl && location.state?.from !== 'edit');
  const backLabel = shouldGoToDashboard ? 'Back to Blogs' : 'Back to Edit Blog';
  const backTarget = shouldGoToDashboard
    ? '/digital-marketing/blog'
    : selectedBlogId
    ? `/digital-marketing/blog/edit/${selectedBlogId}`
    : '/digital-marketing/blog/create';

  return (
    <DMDiv
      className={
        currentView === 'dashboard'
          ? 'flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter'
          : 'relative px-6 py-8 font-sans'
      }
    >
      {currentView === 'dashboard' && (
        <BlogDashboardView
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onNewBlog={() => {
            setSelectedBlogId(null);
            navigate('/digital-marketing/blog/create');
          }}
          publishFilter={publishFilter}
          handleFilterChange={handleFilterChange}
          isLoadingBlogs={isLoadingBlogs}
          blogs={blogsList}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={handleBlogPageChange}
          onPageSizeChange={handleBlogPageSizeChange}
          handleEdit={handleEdit}
          handlePreview={handlePreview}
          onDelete={id => {
            setSelectedBlogId(id);
            setActiveModal('delete');
          }}
          onPublish={id => requestPublishStatusChange(id, true)}
          onUnpublish={id => requestPublishStatusChange(id, false)}
        />
      )}

      {currentView === 'create' &&
        (isLoadingBlogView ? (
          <DMDiv className="flex items-center justify-center min-h-[400px]">
            <Spinner />
          </DMDiv>
        ) : (
          <BlogFormView
            isEditMode={isEditMode}
            blogForm={blogForm}
            setBlogForm={setBlogForm}
            handleGenerateText={handleGenerateText}
            isGeneratingAIText={isGeneratingAIText}
            hasAIGeneratedText={hasAIGeneratedText}
            handleGenerateImage={handleGenerateImage}
            isGeneratingAIImage={isGeneratingAIImage}
            hasAIGeneratedImage={hasAIGeneratedImage}
            isVerifyingImage={isVerifyingImage}
            verificationError={verificationError}
            isUploadingImage={isUploadingImage}
            setIsUploadingImage={setIsUploadingImage}
            isEnhancingImage={isEnhancingImage}
            handleEnhanceImage={() =>
              blogForm.image && enhanceImage(blogForm.image)
            }
            onPreview={() =>
              navigate('/digital-marketing/blog/preview', {
                state: {
                  from: 'edit',
                  blogForm,
                  hasAIGeneratedText,
                  hasAIGeneratedImage,
                },
              })
            }
          />
        ))}

      {currentView === 'preview' &&
        (isLoadingBlogView ? (
          <DMDiv className="flex items-center justify-center min-h-[400px]">
            <Spinner />
          </DMDiv>
        ) : (
          <BlogPreviewView
            backTarget={backTarget}
            backLabel={backLabel}
            onBack={() =>
              navigate(backTarget, {
                state: {
                  restore: true,
                  blogForm,
                  hasAIGeneratedText,
                  hasAIGeneratedImage,
                },
              })
            }
            isEditMode={isEditMode}
            blogForm={blogForm}
            fromDashboard={fromDashboard}
            isPublished={isPublished}
            selectedBlogId={selectedBlogId}
            isUpsertingBlog={isUpsertingBlog}
            isPublishingBlog={isPublishingBlog}
            isPublishingFlow={isPublishingFlow}
            handleSaveDraft={() => {
              setIsPublishingFlow(false);
              handleSaveDraft();
            }}
            onPublish={() => {
              if (
                selectedBlogId &&
                !hasBlogFormChanges(blogForm, currentBlog)
              ) {
                setIsPublishingFlow(false);
                requestPublishStatusChange(selectedBlogId, true);
              } else {
                showGlobalModal(
                  'Are you sure you want to publish this blog?',
                  'warning',
                  {
                    showConfirm: true,
                    onConfirm: () => {
                      hasRequestedPublishAfterUpsert.current = false;
                      setIsPublishingFlow(true);
                      handleSaveDraft();
                    },
                    onCancel: () => undefined,
                    confimLabel: 'Publish',
                    cancelLabel: 'Cancel',
                  }
                );
              }
            }}
            onUnpublish={id => requestPublishStatusChange(id, false)}
          />
        ))}

      <GeneralModals
        activeModal={activeModal}
        setActiveModal={setActiveModal}
        isEditMode={isEditMode}
        isDeletingBlog={isDeletingBlog}
        handleDeleteConfirm={handleDeleteConfirm}
        warningMessage={warningMessage}
      />
    </DMDiv>
  );
}
